import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type MasterProject = { id?: number; nombre?: string; [key: string]: unknown };
export type Project = { id?: number; nombre?: string; idMacroProyecto?: number; [key: string]: unknown };

type Props = {
  masterProjects: MasterProject[];
  projects: Project[];
  selectedMasterProjectId: string | null;
  selectedProjectId: string | null;
  onMacroproyectosLoad: () => void;
  onProyectosLoad: (idMacro: string) => void;
  onMacroSelect: (id: string) => void;
  onProyectoSelect: (id: string) => void;
  onConfirm: () => void;
  isLoadingMacros?: boolean;
  isLoadingProyectos?: boolean;
};

const MacroproyectoProyectoSelector = ({
  masterProjects,
  projects,
  selectedMasterProjectId,
  selectedProjectId,
  onMacroproyectosLoad,
  onProyectosLoad,
  onMacroSelect,
  onProyectoSelect,
  onConfirm,
  isLoadingMacros,
  isLoadingProyectos,
}: Props) => {
  useEffect(() => {
    onMacroproyectosLoad();
  }, [onMacroproyectosLoad]);

  useEffect(() => {
    if (selectedMasterProjectId) {
      onProyectosLoad(selectedMasterProjectId);
    }
  }, [selectedMasterProjectId, onProyectosLoad]);

  const selectedProject = projects.find(
    (p) => String(p.id ?? p.Id ?? "") === selectedProjectId
  );

  const getMasterProjectId = (m: MasterProject) => String(m.id ?? m.Id ?? "");
  const getMasterProjectName = (m: MasterProject): string =>
    String(m.nombre ?? (m as { Nombre?: string }).Nombre ?? "Sin nombre");
  const getProjectId = (p: Project) => String(p.id ?? p.Id ?? "");
  const getProjectName = (p: Project): string =>
    String(p.nombre ?? (p as { Nombre?: string }).Nombre ?? "Sin nombre");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Macroproyecto y Proyecto</CardTitle>
        <CardDescription>
          Selecciona el macroproyecto y luego el proyecto sobre el cual ejecutar el recaudo
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Macroproyecto</Label>
          {isLoadingMacros ? (
            <div className="flex min-h-[40px] items-center">
              <Spinner />
            </div>
          ) : (
            <Select
              value={selectedMasterProjectId ?? ""}
              onValueChange={(v) => {
                onMacroSelect(v);
                onProyectoSelect("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un macroproyecto" />
              </SelectTrigger>
              <SelectContent>
                {masterProjects.map((m) => (
                  <SelectItem key={getMasterProjectId(m)} value={getMasterProjectId(m)}>
                    {getMasterProjectName(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className={cn("space-y-2", !selectedMasterProjectId && "opacity-50")}>
          <Label>Proyecto</Label>
          {selectedMasterProjectId && isLoadingProyectos ? (
            <div className="flex min-h-[40px] items-center">
              <Spinner />
            </div>
          ) : (
            <Select
              value={selectedProjectId ?? ""}
              onValueChange={onProyectoSelect}
              disabled={!selectedMasterProjectId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un proyecto" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={getProjectId(p)} value={getProjectId(p)}>
                    {getProjectName(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button onClick={onConfirm} disabled={!selectedProject}>
          Continuar
        </Button>
      </CardContent>
    </Card>
  );
};

export default MacroproyectoProyectoSelector;
